import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { NoteEditorStyles as styles } from "./NoteEditorStyle";
import { useApiGetAllRemarksTemplate } from "../hooks/api/useApiGetAllRemarksTemplate";
import {
  RequestCreateRemarksTemplateRequest,
  RequestDeleteRemarksTemplateRequest,
  RequestGetRemarksTemplateRequest,
  RequestUpdateRemarksTemplateRequest,
  ResponseGetRemarksTemplateResponse,
  ResponseLoginResponse,
} from "../services/OpenAPI";
import RemarksTemplateDialog from "./RemarksTemplateDialog";
import { Status } from "../constants/status";
import { templateType } from "../constants/templateType";
import { useApiCreateRemarksTemplate } from "../hooks/api/useApiCreateRemarksTemplate";
import { useApiUpdateRemarksTemplate } from "../hooks/api/useApiUpdateRemarksTemplate";
import { useApiDeleteRemarksTemplate } from "../hooks/api/useApiDeleteRemarksTemplate";
import { isGeneralUser, isNotGeneralUser } from "../constants/roles";

type Note = {
  timestamp: string;
  user: string;
  content: string;
  imageUrl?: string;
};

type NoteEditorProps = {
  userName?: string;
  content?: string;
  notes: Note[];
  setNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  isDisabled?: boolean;
  loginUser: ResponseLoginResponse | null;
  statusId?: number;
};

const NoteEditor = ({
  userName = "",
  content = "",
  notes,
  setNotes,
  isDisabled = false,
  loginUser,
  statusId,
}: NoteEditorProps) => {
  const { fetchRemarksTemplateList } = useApiGetAllRemarksTemplate();
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [remarksTemplateDialogOpen, setRemarksTemplateDialog] = useState(false);
  const [remarksTemplateList, setRemarksTemplateList] = useState<
    ResponseGetRemarksTemplateResponse[] | null
  >([]);
  const { createRemarksTemplate } = useApiCreateRemarksTemplate();
  const { updateRemarksTemplate } = useApiUpdateRemarksTemplate();
  const { deleteRemarksTemplate } = useApiDeleteRemarksTemplate();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // 画像プレビュー用
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  // 実際のファイル保持用
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [openImage, setOpenImage] = useState<string | null>(null);
  const [mode, setMode] = useState<templateType>(templateType.LIST);

  const AddList = (str: string, imageFile?: File) => {
    const now = new Date();
    const timestamp = now
      .toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-");

    const newNote: Note = {
      timestamp,
      user: userName,
      content: str,
      imageUrl: imageFile ? URL.createObjectURL(imageFile) : undefined, // ★画像があればURL化
    };

    setNotes((prev) => [...prev, newNote]);
    setInput("");
    setSelectedImage(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    inputRef.current?.focus();
  };

  const handleAddNote = () => {
    if (!input.trim() && !imageFile) return;
    AddList(input, imageFile ?? undefined);
  };

  // 画像ファイル選択ハンドラ
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
    }
  };

  useEffect(() => {
    if (
      content === null ||
      content === undefined ||
      content === "" ||
      content === "null" ||
      content === "undefined"
    ) {
      return;
    }

    try {
      const notes: Note[] = content.split("\n").map((line) => {
        const match = line.match(
          /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) (.+?)：(.+?)(?: \| imageUrl=(.+))?$/,
        );
        if (!match) throw new Error(`Invalid format: ${line}`);

        return {
          timestamp: match[1],
          user: match[2],
          content: match[3],
          imageUrl: match[4] || undefined,
        };
      });
      setNotes(notes);
    } catch (err) {
      console.error("備考パースエラー:", err);
    }
  }, [content, setNotes]);

  const fetchRemarkstemplete = async () => {
    const req: RequestGetRemarksTemplateRequest = {
      baseId: 1,
      tenantId: 1,
      userId: loginUser?.id ?? 0,
    };
    const response = await fetchRemarksTemplateList(req);
    if (response) {
      setRemarksTemplateList(response);
    }
  };

  useEffect(() => {
    if (remarksTemplateList && remarksTemplateList.length > 0) {
      setRemarksTemplateDialog(true);
    }
  }, [remarksTemplateList]);

  const handleRemarksTemplateSelect = (
    item: ResponseGetRemarksTemplateResponse,
  ) => {
    AddList(item.template!);
    setRemarksTemplateDialog(false);
  };

  const onCreate = async (title: string, template: string) => {
    const req: RequestCreateRemarksTemplateRequest = {
      baseId: loginUser?.baseId!,
      tenantId: loginUser?.tenantId!,
      title,
      template,
      userId: loginUser?.id!,
    };
    const response = await createRemarksTemplate(req);
    if (response) {
      fetchRemarkstemplete();
    }
  };

  const onUpdate = async (id: number, title: string, template: string) => {
    const req: RequestUpdateRemarksTemplateRequest = {
      id,
      baseId: loginUser?.baseId!,
      tenantId: loginUser?.tenantId!,
      title,
      template,
      userId: loginUser?.id!,
    };
    const response = await updateRemarksTemplate(req);
    if (response) {
      fetchRemarkstemplete();
    }
  };

  const onDelete = async (ids: number[]) => {
    const req: RequestDeleteRemarksTemplateRequest = {
      ids,
      baseId: loginUser?.baseId!,
      tenantId: loginUser?.tenantId!,
      userId: loginUser?.id!,
    };
    const response = await deleteRemarksTemplate(req);
    if (response) {
      fetchRemarkstemplete();
    }
  };

  return (
    <>
      <RemarksTemplateDialog
        open={remarksTemplateDialogOpen}
        onClose={() => setRemarksTemplateDialog(false)}
        list={remarksTemplateList!}
        onSelect={(item) => {
          AddList(item.template!, imageFile ?? undefined);
          setRemarksTemplateDialog(false);
        }}
        mode={mode}
        setMode={setMode}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onDelete={onDelete}
        loginUser={loginUser}
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth={false}
        PaperProps={{
          style: {
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: 0,
            width: "70%",
            height: "70%",
          },
        }}
      >
        <img
          src={openImage ?? ""}
          alt="拡大画像"
          style={{
            width: "60vw",
            height: "60vh",
            objectFit: "contain",
          }}
        />
      </Dialog>
      <Box sx={styles.container}>
        <Box sx={styles.titleBar}>
          <Typography variant="h6" sx={styles.titleText}>
            備考
          </Typography>
        </Box>

        {/* 既存のメモ一覧 */}
        <Box px={2} py={2}>
          {notes.map((note, index) => (
            <Box key={index} mb={3}>
              <Typography sx={styles.noteEntry}>
                {note.timestamp} {note.user}：{note.content}
              </Typography>
              {note.imageUrl && (
                <Box mt={1}>
                  <img
                    src={note.imageUrl}
                    alt="note attachment"
                    style={{
                      maxWidth: "800px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                    onClick={() => setOpenImage(note.imageUrl!)}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* 入力欄 */}
        <Box sx={styles.inputArea}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputRef={inputRef}
            placeholder="備考を入力してください"
            disabled={
              isGeneralUser(loginUser?.permissionId) &&
              statusId !== undefined &&
              statusId !== Status.PendingReceipt
            }
          />

          {/* 画像ファイル入力 */}
          <Box mt={1}>
            <input
              type="file"
              id="upload"
              accept="image/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setImageFile(e.target.files[0]);
                } else {
                  setImageFile(null);
                  setSelectedImage(null);
                }
              }}
            />
            <label htmlFor="upload">
              <Button
                variant="contained"
                component="span"
                disabled={
                  isGeneralUser(loginUser?.permissionId) &&
                  statusId !== undefined &&
                  statusId !== Status.PendingReceipt
                }
              >
                画像を選択
              </Button>
            </label>
            {imageFile && (
              <Box mt={1}>
                <img
                  src={URL.createObjectURL(imageFile)}
                  alt="プレビュー"
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* ボタン行 */}
        <Box sx={styles.buttonRow}>
          {isNotGeneralUser(loginUser?.permissionId) && (
            <Tooltip title="画像を添付する場合は先に「画像を選択」から操作してください">
              <Button
                variant="contained"
                sx={styles.orangeButton}
                onClick={() => fetchRemarkstemplete()}
              >
                テンプレートで追加
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            sx={styles.orangeButton}
            onClick={handleAddNote}
            disabled={
              isGeneralUser(loginUser?.permissionId) &&
              statusId !== undefined &&
              statusId !== Status.PendingReceipt
            }
          >
            備考を追加
          </Button>
        </Box>
      </Box>
    </>
  );
};

export type { Note };
export default NoteEditor;
